import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Fingerprint, Loader2 } from "lucide-react";
import { startRegistration } from "@simplewebauthn/browser";
import { toast } from "sonner";
import { getWebAuthnErrorMessage } from "@/utils/webauthn-errors";

export function WebAuthnPrompt() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    async function checkPromptStatus() {
      if (!user) {
        setChecking(false);
        return;
      }

      try {
        // Check if user is a workspace admin
        const { data: memberData, error: memberError } = await supabase
          .from("workspace_members")
          .select("role")
          .eq("user_id", user.id)
          .eq("role", "admin")
          .maybeSingle();

        if (memberError) throw memberError;

        // Only proceed if the user is a workspace admin
        if (memberData) {
          // Check if they have already been prompted
          const { data: profileData, error: profileError } = await supabase
            .from("profiles")
            .select("webauthn_prompted")
            .eq("user_id", user.id)
            .maybeSingle();

          if (profileError) throw profileError;

          if (profileData && !profileData.webauthn_prompted) {
            // Check if they already have credentials registered
            const { count, error: credError } = await supabase
              .from("webauthn_credentials")
              .select("id", { count: 'exact', head: true });

            if (credError) throw credError;

            if (count === 0) {
              setOpen(true);
            } else {
              // Already has credentials, mark as prompted
              await supabase
                .from("profiles")
                .update({ webauthn_prompted: true })
                .eq("user_id", user.id);
            }
          }
        }
      } catch (error) {
        console.error("Error checking WebAuthn prompt status:", error);
      } finally {
        setChecking(false);
      }
    }

    checkPromptStatus();
  }, [user]);

  const handleDecline = async () => {
    setOpen(false);
    if (user) {
      await supabase
        .from("profiles")
        .update({ webauthn_prompted: true })
        .eq("user_id", user.id);
    }
  };

  const handleEnable = async () => {
    if (typeof window !== "undefined" && window.self !== window.top) {
      toast.error(getWebAuthnErrorMessage("Permissions Policy"));
      return;
    }

    setLoading(true);
    try {
      const { data: options, error: optErr } = await supabase.functions.invoke("webauthn-register-options");
      if (optErr || !options) throw new Error(optErr?.message || "No se pudieron obtener las opciones");

      const attResp = await startRegistration(options);

      const { data: verifyData, error: verErr } = await supabase.functions.invoke("webauthn-register-verify", {
        body: { 
          response: attResp, 
          device_name: `Acceso rápido (${new Date().toLocaleDateString()})` 
        },
      });

      if (verErr || !(verifyData as any)?.verified) {
        throw new Error(verErr?.message || (verifyData as any)?.error || "Verificación fallida");
      }

      toast.success("Huella registrada correctamente");
      setOpen(false);
      
      // Mark as prompted after successful registration
      if (user) {
        await supabase
          .from("profiles")
          .update({ webauthn_prompted: true })
          .eq("user_id", user.id);
          
        await supabase.from('security_logs').insert({
          user_id: user.id,
          event_type: 'webauthn_registration_prompt',
          status: 'success',
          description: 'Registro exitoso desde el aviso inicial'
        });
      }
    } catch (e: any) {
      toast.error(getWebAuthnErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  if (checking || !open) return null;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleDecline()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Fingerprint className="h-6 w-6 text-primary" />
            Activar acceso con huella
          </DialogTitle>
          <DialogDescription>
            Como administrador, puedes activar el acceso con huella (WebAuthn) para iniciar sesión de forma más rápida y segura.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col items-center justify-center py-4 space-y-4">
          <div className="bg-primary/10 p-4 rounded-full">
            <Fingerprint className="h-12 w-12 text-primary" />
          </div>
          <p className="text-sm text-center text-muted-foreground">
            Podrás activar o desactivar esta opción en cualquier momento desde la configuración de seguridad.
          </p>
        </div>
        <DialogFooter className="flex sm:justify-between gap-2">
          <Button variant="ghost" onClick={handleDecline} disabled={loading}>
            Ahora no
          </Button>
          <Button onClick={handleEnable} disabled={loading} className="gap-2">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Fingerprint className="h-4 w-4" />}
            Activar ahora
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
